import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const WAVE_COUNT = 3;
const LINKEDIN_BLUE = '#0A66C2';

const TeamMemberCard = ({
  member,
  isExpanded,
  isFocused,
  hasRowFocus,
  onHoverStart,
  onHoverEnd,
  onToggleExpand,
}) => {
  const { t } = useTranslation();
  const [avatarHover, setAvatarHover] = useState(false);
  const baseKey = `meetTheTeam.members.${member.id}`;

  const bio = t(`${baseKey}.bio`);
  const affiliation = t(`${baseKey}.affiliation`, { defaultValue: '' });
  const education = t(`${baseKey}.education`, { returnObjects: true, defaultValue: [] });
  const awards = t(`${baseKey}.awards`, { returnObjects: true, defaultValue: [] });
  const research = t(`${baseKey}.research`, { returnObjects: true, defaultValue: [] });

  const educationItems = Array.isArray(education) ? education : [];
  const awardItems = Array.isArray(awards) ? awards : [];
  const researchItems = Array.isArray(research) ? research : [];

  const hasDescription = !!(bio || affiliation);
  const hasExperience =
    educationItems.length > 0 || awardItems.length > 0 || researchItems.length > 0;

  const showWaves = avatarHover || isExpanded;
  const accent = member.accentColor;

  const renderExperience = () => (
    <>
      {educationItems.length > 0 && (
        <div>
          <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wide text-ink-subtle sm:text-xs">
            {t('meetTheTeam.sections.education')}
          </h3>
          <ul className="space-y-1.5">
            {educationItems.map((item, index) => (
              <li key={`${member.id}-edu-${index}`}>
                <p className="font-medium text-ink team-prose">{item.degree}</p>
                <p className="text-[0.65rem] text-ink-subtle sm:text-xs">{item.institution}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {awardItems.length > 0 && (
        <div className={educationItems.length > 0 ? 'mt-3' : ''}>
          <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wide text-ink-subtle sm:text-xs">
            {t('meetTheTeam.sections.featuredAwards')}
          </h3>
          <ul className="list-disc space-y-0.5 pl-4 text-[0.65rem] sm:text-xs">
            {awardItems.map((award, index) => (
              <li key={`${member.id}-award-${index}`}>{award}</li>
            ))}
          </ul>
        </div>
      )}

      {researchItems.length > 0 && (
        <div className={educationItems.length > 0 || awardItems.length > 0 ? 'mt-3' : ''}>
          <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wide text-ink-subtle sm:text-xs">
            {t('meetTheTeam.sections.featuredResearch')}
          </h3>
          <ul className="space-y-1 text-[0.65rem] sm:text-xs">
            {researchItems.map((entry, index) => (
              <li key={`${member.id}-research-${index}`} className="leading-relaxed">
                {entry}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );

  return (
    <article
      className={[
        'team-member-slot flex min-w-0 flex-col items-center',
        isExpanded && 'team-member-slot--expanded',
        showWaves && 'team-member-slot--halo-active',
        hasRowFocus && isFocused && 'team-member-slot--focused',
        hasRowFocus && !isFocused && 'team-member-slot--dimmed',
      ]
        .filter(Boolean)
        .join(' ')}
      onMouseEnter={onHoverStart ? () => onHoverStart(member.id) : undefined}
      onMouseLeave={onHoverEnd ?? undefined}
    >
      <div
        className={[
          'relative mb-6 shrink-0 flex w-full items-center justify-center overflow-visible px-6 pb-4 pt-2 sm:mb-7 sm:px-8 sm:pb-5',
          showWaves && 'z-30',
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseEnter={() => setAvatarHover(true)}
        onMouseLeave={() => setAvatarHover(false)}
      >
        <div className="relative isolate h-[5.5rem] w-[5.5rem] overflow-visible sm:h-24 sm:w-24 lg:h-28 lg:w-28">
          {showWaves &&
            Array.from({ length: WAVE_COUNT }, (_, index) => (
              <span
                key={index}
                className="team-avatar-wave pointer-events-none absolute inset-0 z-0 rounded-full border-2"
                style={{
                  borderColor: accent,
                  animationDelay: `${index * 0.8}s`,
                }}
                aria-hidden
              />
            ))}

          <a
            href={member.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className={`relative z-10 block h-full w-full overflow-hidden rounded-full bg-surface-muted shadow-sm ring-2 ring-white transition-transform duration-500 ease-out motion-reduce:transition-none ${
              showWaves ? 'scale-105' : 'scale-100'
            }`}
            aria-label={`${t(`${baseKey}.name`)} — ${t('meetTheTeam.viewOnLinkedIn')}`}
          >
            <img
              src={member.image}
              alt=""
              className="h-full w-full object-cover object-top"
              loading="lazy"
            />
          </a>
        </div>
      </div>

      <div
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onClick={() => onToggleExpand(member.id)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggleExpand(member.id);
          }
        }}
        className={`team-member-card w-full cursor-pointer rounded-2xl border bg-surface px-3 py-3 text-center shadow-sm max-md:transition-[border-color,box-shadow] md:transition-all duration-500 ease-out motion-reduce:transition-none sm:px-4 ${
          isExpanded
            ? 'border-edge-strong shadow-md'
            : 'border-edge hover:border-edge-strong hover:shadow-md'
        } ${isFocused && hasRowFocus ? 'md:px-5' : ''}`}
      >
        <h2 className="shrink-0 text-sm font-semibold leading-tight text-ink sm:text-base">
          {t(`${baseKey}.name`)}
        </h2>
        {/* Temporarily hidden — avoid implying a paid/commercial service via job titles */}
        {/* <p className="mt-1 shrink-0 text-xs leading-snug text-ink-muted sm:text-sm">{t(`${baseKey}.role`)}</p> */}

        <div
          className={`team-member-expand-grid grid transition-[grid-template-rows] duration-500 ease-out motion-reduce:transition-none ${
            isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          }`}
          aria-hidden={!isExpanded}
        >
          <div className="team-member-expand-inner min-h-0 overflow-hidden">
            <div className="team-member-expand-content pt-4">
              {hasDescription && (
                <div className="team-prose shrink-0 px-0.5 text-left text-xs leading-relaxed text-ink-muted sm:text-sm">
                  {bio && <p>{bio}</p>}
                  {affiliation && <p className={`text-ink-muted ${bio ? 'mt-2' : ''}`}>{affiliation}</p>}
                </div>
              )}

              <a
                href={member.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => event.stopPropagation()}
                className="team-linkedin-btn mt-4 inline-flex w-full shrink-0 items-center justify-center gap-2.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-300 ease-out"
              >
                <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.135 1.445-2.135 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                {t('meetTheTeam.viewOnLinkedIn')}
              </a>

              {hasExperience && (
                <div
                  className={`team-panel-stage team-panel-scroll relative mt-4 text-left ${
                    isExpanded ? 'team-panel-stage--expanded' : ''
                  }`}
                >
                  <div className="team-prose px-0.5 text-xs leading-relaxed text-ink-muted sm:text-sm">
                    {renderExperience()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export default TeamMemberCard;
